import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faShoppingCart, faMoneyBillWave, faCheckCircle, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { getPlatformIcon, getPlatformColor } from '../platformIcons';
import Modal from '../components/Modal';
import './NewOrder.css';

const API = process.env.REACT_APP_API_URL || 'https://api.bustisha.com/api';

export default function NewOrder() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [analytics, setAnalytics] = useState({ total_orders: 0, completed_orders: 0, total_spent: 0, balance: 0 });
  const [services, setServices] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [activePlatform, setActivePlatform] = useState('');
  const [activeCategory, setActiveCategory] = useState('');
  const [search, setSearch] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [link, setLink] = useState('');
  const [quantity, setQuantity] = useState('');
  const [catOpen, setCatOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const catRef = useRef(null);
  const svcRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/settings/metadata`)
      .then((r) => r.json())
      .then((d) => setPlatforms(Array.isArray(d.platforms) ? d.platforms : []))
      .catch(() => {});
    fetch(`${API}/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => { if (d.stats) setAnalytics(d.stats); })
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setServicesLoading(true);
    const params = new URLSearchParams();
    if (activePlatform) params.set('platform', activePlatform);
    if (activeCategory) params.set('category', activeCategory);
    if (search) params.set('search', search);
    const timer = setTimeout(() => {
      fetch(`${API}/services?${params}`)
        .then((r) => r.json())
        .then((data) => setServices(Array.isArray(data) ? data : []))
        .catch(() => setServices([]))
        .finally(() => setServicesLoading(false));
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [activePlatform, activeCategory, search]);

  useEffect(() => {
    if (platforms.length === 0 && !activePlatform) {
      const derived = [...new Set(services.map((s) => s.platform))].sort();
      if (derived.length > 0) setPlatforms(derived);
    }
  }, [services]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClick = (e) => {
      if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false);
      if (svcRef.current && !svcRef.current.contains(e.target)) setSvcOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const allCategories = [...new Set(services.map((s) => s.category))];

  const filteredServices = services.filter((s) => {
    const matchCategory = activeCategory ? s.category === activeCategory : true;
    const matchSearch = search ? s.name.toLowerCase().includes(search.toLowerCase()) : true;
    return matchCategory && matchSearch;
  });

  const [orderLoading, setOrderLoading] = useState(false);
  const [modal, setModal] = useState({ open: false, type: 'info', title: '', message: '' });

  const total = selectedService && quantity
    ? ((Number(selectedService.rate) * parseInt(quantity)) / 1000).toFixed(2)
    : '0.00';

  const placeOrder = async () => {
    if (!selectedService || !link || !quantity) return;
    setOrderLoading(true);
    try {
      const res = await fetch(`${API}/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ service_id: selectedService.id, link, quantity: parseInt(quantity) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Order failed');
      setModal({ open: true, type: 'success', title: 'Order Placed!', message: `Order #${data.id} has been placed successfully.` });
      setLink('');
      setQuantity('');
      setSelectedService(null);
    } catch (err) {
      if (err.message === 'Insufficient balance.') {
        navigate('/add-fund');
        return;
      }
      setModal({ open: true, type: 'error', title: 'Order Failed', message: err.message });
    } finally {
      setOrderLoading(false);
    }
  };

  return (
    <div className="new-order">
      <div className="analytics-row">
        <div className="analytic-card">
          <div className="analytic-icon orders">
            <FontAwesomeIcon icon={faShoppingCart} />
          </div>
          <div className="analytic-info">
            <span className="analytic-value">{analytics.total_orders}</span>
            <span className="analytic-label">Total Orders</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon completed">
            <FontAwesomeIcon icon={faCheckCircle} />
          </div>
          <div className="analytic-info">
            <span className="analytic-value">{analytics.completed_orders}</span>
            <span className="analytic-label">Completed</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon spent">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div className="analytic-info">
            <span className="analytic-value">TSHS {Number(analytics.total_spent || 0).toLocaleString()}</span>
            <span className="analytic-label">Total Spent</span>
          </div>
        </div>
        <div className="analytic-card">
          <div className="analytic-icon spent">
            <FontAwesomeIcon icon={faMoneyBillWave} />
          </div>
          <div className="analytic-info">
            <span className="analytic-value">TSHS {Number(analytics.balance || 0).toLocaleString()}</span>
            <span className="analytic-label">Balance</span>
          </div>
        </div>
      </div>

      <div className="section-label">Platforms</div>
      <div className="platform-filters">
        <button
          className={`platform-chip ${activePlatform === '' ? 'active' : ''}`}
          onClick={() => setActivePlatform('')}
        >
          All
        </button>
        {platforms.map((name) => (
          <button
            key={name}
            className={`platform-chip ${activePlatform === name ? 'active' : ''}`}
            onClick={() => setActivePlatform(activePlatform === name ? '' : name)}
            style={activePlatform === name ? { borderColor: getPlatformColor(name), background: `${getPlatformColor(name)}20` } : {}}
          >
            <FontAwesomeIcon icon={getPlatformIcon(name)} style={{ color: getPlatformColor(name) }} />
            <span>{name}</span>
          </button>
        ))}
      </div>

      <div className="search-bar">
        <FontAwesomeIcon icon={faSearch} className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder="Search services..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="section-label">Categories</div>
      <div className="dropdown" ref={catRef} onClick={() => setCatOpen(!catOpen)}>
        <span className="dropdown-selected">
          {activeCategory || 'All Categories'}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`dropdown-arrow ${catOpen ? 'open' : ''}`} />
        {catOpen && (
          <div className="dropdown-menu">
            <div
              className={`dropdown-item ${activeCategory === '' ? 'active' : ''}`}
              onClick={(e) => { e.stopPropagation(); setActiveCategory(''); setCatOpen(false); }}
            >
              All Categories
            </div>
            {allCategories.map((cat) => (
              <div
                key={cat}
                className={`dropdown-item ${activeCategory === cat ? 'active' : ''}`}
                onClick={(e) => { e.stopPropagation(); setActiveCategory(cat); setCatOpen(false); }}
              >
                {cat}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="section-label">Services</div>
      <div className="dropdown" ref={svcRef} onClick={() => setSvcOpen(!svcOpen)}>
        <span className="dropdown-selected">
          {selectedService ? selectedService.name : 'Select a service...'}
        </span>
        <FontAwesomeIcon icon={faChevronDown} className={`dropdown-arrow ${svcOpen ? 'open' : ''}`} />
        {svcOpen && (
          <div className="dropdown-menu">
            {servicesLoading ? (
              <div className="dropdown-item empty">Loading services...</div>
            ) : filteredServices.length === 0 ? (
              <div className="dropdown-item empty">No services found</div>
            ) : (
              filteredServices.map((service) => (
                  <div
                    key={service.id}
                    className={`dropdown-item service-item ${selectedService?.id === service.id ? 'active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); setSelectedService(service); setSvcOpen(false); }}
                  >
                    <div className="service-item-top">
                      <FontAwesomeIcon icon={getPlatformIcon(service.platform)} style={{ color: getPlatformColor(service.platform), marginRight: 8 }} />
                      <span className="service-item-name">{service.name}</span>
                      <span className="service-item-cat">{service.category}</span>
                    </div>
                    <div className="service-item-bottom">
                      <span className="service-item-rate">TSHS {Number(service.rate).toLocaleString()} / 1000</span>
                      <span className="service-item-range">Min: {Number(service.min_quantity).toLocaleString()} - Max: {Number(service.max_quantity).toLocaleString()}</span>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}
      </div>

      {selectedService && (
        <div className="order-form">
          <h2 className="form-title">Place Order</h2>
          <div className="form-selected">
            <span className="form-selected-label">Service:</span>
            <span className="form-selected-name">{selectedService.name}</span>
          </div>

          <div className="form-group">
            <label className="form-label">Link / URL</label>
            <input
              type="text"
              className="form-input"
              placeholder="https://instagram.com/username"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input
                type="number"
                className="form-input"
                placeholder={`Min: ${selectedService.min_quantity}`}
                min={selectedService.min_quantity}
                max={selectedService.max_quantity}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Total Cost</label>
              <div className="form-total">TSHS {total}</div>
            </div>
          </div>

          <button className="submit-btn" onClick={placeOrder} disabled={orderLoading}>
            {orderLoading ? 'Placing Order...' : 'Place Order'}
          </button>
        </div>
      )}

      <Modal open={modal.open} type={modal.type} title={modal.title} message={modal.message} onClose={() => setModal({ ...modal, open: false })} />
    </div>
  );
}
